<template>
  <div v-if="serialNumber" class="device-feature-block">
    <div class="detail-section">
      <h3 class="section-title">
        <v-icon left small color="deep-purple">mdi-puzzle</v-icon>
        Fonctionnalités (feature)
        <v-progress-circular v-if="loading" indeterminate size="20" width="2" class="ml-2" color="primary" />
      </h3>
      <div v-if="!loading && rows.length === 0" class="no-data">Aucune souscription fonctionnalité pour cet appareil</div>
      <div v-else-if="rows.length" class="subscriptions-list">
        <v-expansion-panels accordion>
          <v-expansion-panel v-for="(row, index) in rows" :key="getSfId(row) || index">
            <v-expansion-panel-header>
              <div class="subscription-header">
                <v-chip x-small :color="sfStatusColor(row)" class="white--text mr-2">
                  {{ getSf(row).status }}
                </v-chip>
                <span class="sub-plan">{{ row.plan ? row.plan.name : 'Feature' }}</span>
                <span class="sub-dates">{{ formatDate(sfStart(row)) }} - {{ formatDate(sfEnd(row)) }}</span>
                <v-chip x-small :color="getPaymentStatusColor(payStatus(row))" class="white--text ml-2">
                  {{ payStatus(row) }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="detail-grid">
                <v-col cols="12" sm="6" md="4" v-if="row.plan">
                  <div class="detail-item">
                    <span class="detail-label">Plan</span>
                    <span class="detail-value">{{ row.plan.name }} ({{ row.plan.feature_code }})</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" v-if="row.plan">
                  <div class="detail-item">
                    <span class="detail-label">Montant plan</span>
                    <span class="detail-value amount-value">{{ formatNumber(row.plan.amount) }} {{ row.plan.currency }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" v-if="sfStart(row) || sfEnd(row)">
                  <div class="detail-item">
                    <span class="detail-label">Début</span>
                    <span class="detail-value">{{ formatDate(sfStart(row)) }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4" v-if="sfStart(row) || sfEnd(row)">
                  <div class="detail-item">
                    <span class="detail-label">Fin</span>
                    <span class="detail-value">{{ formatDate(sfEnd(row)) }}</span>
                  </div>
                </v-col>
                <template v-if="row.payment_request">
                  <v-col cols="12"><v-divider class="my-3"></v-divider><h4 class="subsection-title">Paiement</h4></v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <span class="detail-label">Commande</span>
                      <span class="detail-value">{{ row.payment_request.order_number }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <span class="detail-label">Montant</span>
                      <span class="detail-value amount-value">{{ formatNumber(row.payment_request.amount) }} {{ row.payment_request.currency }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="detail-item">
                      <span class="detail-label">Statut</span>
                      <div class="payment-status-cell">
                        <v-chip x-small :color="getPaymentStatusColor(row.payment_request.status)" class="white--text">
                          {{ row.payment_request.status }}
                        </v-chip>
                        <v-btn
                          v-if="row.payment_request.status === 'PENDING'"
                          icon
                          x-small
                          color="primary"
                          class="verify-btn ml-1"
                          :loading="verifyingPayments[row.payment_request.id]"
                          @click="verifyPayment(row)"
                        >
                          <v-icon x-small>mdi-refresh</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </template>
                <v-col cols="12" v-if="isFeatureActive(row)" class="transfer-action-col">
                  <v-divider class="my-3"></v-divider>
                  <v-btn color="primary" outlined small class="transfer-btn" @click.stop="openTransfer(row)">
                    <v-icon left small>mdi-swap-horizontal</v-icon>
                    Transférer
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>

    <v-dialog v-model="transferDialog" max-width="560px" scrollable content-class="transfer-feature-dialog">
      <v-card v-if="transferRow && serialNumber">
        <v-card-title class="modal-header transfer-modal-header">
          <div class="modal-title">
            <v-icon left color="primary">mdi-swap-horizontal</v-icon>
            Transférer la fonctionnalité
          </div>
          <v-btn icon @click="closeTransfer"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="transfer-modal-body">
          <div class="detail-section transfer-origin mb-3">
            <h4 class="subsection-title">Appareil d’origine</h4>
            <p class="mb-0 text-caption text--secondary">S/N : {{ serialNumber }}</p>
            <p v-if="transferRow.plan" class="mt-2 mb-0">Plan : {{ transferRow.plan.name }}</p>
          </div>
          <v-text-field
            v-model="transferSearch"
            prepend-inner-icon="mdi-magnify"
            label="Rechercher un appareil"
            outlined
            dense
            hide-details
            class="mb-3"
            clearable
          />
          <p class="text-caption text--secondary mb-2">{{ filteredDevices.length }} appareil(s)</p>
          <v-list dense class="transfer-device-list" v-if="filteredDevices.length">
            <v-list-item
              v-for="d in filteredDevices"
              :key="d.id"
              @click="transferTarget = d"
              :class="{ 'transfer-device-selected': transferTarget && transferTarget.id === d.id }"
            >
              <v-list-item-content>
                <v-list-item-title>{{ d.name || d.serial_number || '—' }}</v-list-item-title>
                <v-list-item-subtitle>S/N : {{ d.serial_number || '—' }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="transferTarget && transferTarget.id === d.id">
                <v-icon color="primary">mdi-check-circle</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-else class="no-data py-4">Aucun autre appareil pour ce compte.</p>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeTransfer">Annuler</v-btn>
          <v-btn color="primary" :disabled="!canSubmitTransfer" :loading="transferLoading" @click="submitTransfer">
            Transférer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DeviceFeatureSubscriptionsBlock',
  props: {
    serialNumber: { type: String, default: '' },
    devicesList: { type: Array, default: function() { return [] } },
    currentDeviceId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,
      rows: [],
      verifyingPayments: {},
      transferDialog: false,
      transferRow: null,
      transferSearch: '',
      transferTarget: null,
      transferLoading: false
    }
  },
  computed: {
    filteredDevices() {
      var self = this
      var sn = String(self.serialNumber || '').trim()
      var q = (self.transferSearch || '').trim().toLowerCase()
      return self.devicesList.filter(function(d) {
        if (self.currentDeviceId != null && d.id === self.currentDeviceId) return false
        if (sn && String(d.serial_number || '').trim() === sn) return false
        if (!q) return true
        var name = (d.name || '').toLowerCase()
        var ds = (d.serial_number || '').toLowerCase()
        var idf = (d.identifier || '').toLowerCase()
        return name.indexOf(q) !== -1 || ds.indexOf(q) !== -1 || idf.indexOf(q) !== -1
      })
    },
    canSubmitTransfer() {
      if (!this.transferTarget) return false
      var s = this.transferTarget.serial_number
      return !!(s && String(s).trim().length > 0)
    }
  },
  watch: {
    serialNumber: {
      immediate: true,
      handler(val) {
        if (val && String(val).trim()) this.load()
        else {
          this.rows = []
        }
      }
    }
  },
  methods: {
    parseFeatureRowsResponse(data) {
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      if (data && data.success && data.result != null) {
        if (Array.isArray(data.result)) return data.result
        if (Array.isArray(data.result.data)) return data.result.data
      }
      return []
    },
    getSf(row) {
      return (row && row.subscription_feature) || row || {}
    },
    getSfId(row) {
      var sf = this.getSf(row)
      return sf.id
    },
    load() {
      var self = this
      var sn = String(self.serialNumber || '').trim()
      if (!sn) return
      self.loading = true
      self.$axios
        .get('/subscription-features', {
          params: {
            serial_number: sn,
            include: 'device.business,business,payment_request'
          }
        })
        .then(function(res) {
          self.rows = self.parseFeatureRowsResponse(res.data)
        })
        .catch(function() {
          self.rows = []
        })
        .finally(function() {
          self.loading = false
        })
    },
    sfStart(row) {
      var sf = this.getSf(row)
      return sf.starts_on
    },
    sfEnd(row) {
      var sf = this.getSf(row)
      return sf.ends_on
    },
    payStatus(row) {
      return (row.payment_request && row.payment_request.status) || '—'
    },
    formatDate(d) {
      if (!d) return '—'
      return moment(d).format('DD/MM/YYYY')
    },
    formatNumber(v) {
      if (v == null || v === '') return '0'
      return parseInt(v, 10).toLocaleString('fr-FR')
    },
    sfStatusColor(row) {
      var s = String((this.getSf(row).status) || '').toUpperCase()
      if (s === 'ACTIVE') return 'success'
      if (s === 'EXPIRED') return 'warning'
      if (s === 'CANCELLED') return 'error'
      return 'grey'
    },
    getPaymentStatusColor(status) {
      if (!status) return 'grey'
      var s = String(status).toUpperCase()
      if (s === 'CONFIRMED') return 'success'
      if (s === 'PENDING') return 'warning'
      if (s === 'DENIED') return 'error'
      return 'grey'
    },
    isFeatureActive(row) {
      var s = this.getSf(row).status
      return String(s || '').toUpperCase() === 'ACTIVE'
    },
    verifyPayment(row) {
      var self = this
      if (!row.payment_request || !row.payment_request.id) return
      var id = row.payment_request.id
      self.$set(self.verifyingPayments, id, true)
      self.$axios
        .get('/payment-requests/' + id)
        .then(function(res) {
          var u = res.data
          if (!u) return
          var idx = self.rows.findIndex(function(r) {
            return r.payment_request && r.payment_request.id === id
          })
          if (idx !== -1) {
            self.$set(self.rows[idx], 'payment_request', Object.assign({}, self.rows[idx].payment_request, u))
          }
        })
        .finally(function() {
          self.$set(self.verifyingPayments, id, false)
        })
    },
    openTransfer(row) {
      this.transferRow = row
      this.transferSearch = ''
      this.transferTarget = null
      this.transferDialog = true
    },
    closeTransfer() {
      this.transferDialog = false
      this.transferRow = null
      this.transferLoading = false
    },
    submitTransfer() {
      var self = this
      if (!self.canSubmitTransfer || !self.transferRow) return
      var sfId = self.getSfId(self.transferRow)
      if (sfId == null) return
      var serial = String(self.transferTarget.serial_number).trim()
      self.transferLoading = true
      self.$axios
        .post('/subscription-features/transfer', {
          subscription_feature_id: sfId,
          serial_number: serial
        })
        .then(function(res) {
          var data = res.data
          if (data && data.success) {
            self.closeTransfer()
            self.$emit('transferred')
            self.load()
          } else {
            alert((data && (data.message || data.error)) || 'Échec du transfert')
          }
        })
        .catch(function(err) {
          var msg = 'Erreur lors du transfert.'
          if (err.response && err.response.data) {
            var d = err.response.data
            msg = d.message || d.error || msg
          }
          alert(msg)
        })
        .finally(function() {
          self.transferLoading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.device-feature-block {
  .detail-section {
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    color: #003f74;
  }

  .subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: #666;
    margin-bottom: 12px;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
  }

  .detail-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
  }

  .detail-value {
    font-size: 14px;
    font-weight: 500;
  }

  .amount-value {
    color: #003f74;
    font-weight: 600;
  }

  .subscription-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }

  .sub-plan {
    font-weight: 500;
    flex: 1;
  }

  .sub-dates {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
  }

  .no-data {
    color: rgba(0, 0, 0, 0.5);
    font-style: italic;
    text-align: center;
    padding: 16px;
  }

  .payment-status-cell {
    display: flex;
    align-items: center;
  }

  .transfer-device-list {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    max-height: 240px;
    overflow-y: auto;
  }

  .transfer-device-selected {
    background: rgba(0, 63, 116, 0.08) !important;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
  }

  .transfer-modal-body {
    padding: 16px 24px !important;
  }

  .modal-actions {
    padding: 12px 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
}

::v-deep .transfer-feature-dialog {
  z-index: 310 !important;
}
</style>
